import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

interface VerificationCodeInputProps {
  label?: string;
  error?: string;
  onChange: (code: string) => void;
  length?: number;
}

export interface VerificationCodeInputRef {
  clear: () => void;
  focus: () => void;
}

const VerificationCodeInput = forwardRef<VerificationCodeInputRef, VerificationCodeInputProps>(
  ({ label, error, onChange, length = 4 }, ref) => {
    const [code, setCode] = useState<string[]>(Array(length).fill(''));
    const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(length).fill(null));

    useImperativeHandle(ref, () => ({
      clear: () => {
        setCode(Array(length).fill(''));
        onChange('');
        inputRefs.current[0]?.focus();
      },
      focus: () => {
        inputRefs.current[0]?.focus();
      },
    }));

    useEffect(() => {
      // Focus first input on mount
      inputRefs.current[0]?.focus();
    }, []);

    const handleInputChange = (index: number, value: string) => {
      if (value.length > 1) return; // Only allow single digits

      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Move to next input if value is entered
      if (value && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Update parent component
      onChange(newCode.join(''));
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
      // Move to previous input on backspace if current input is empty
      if (e.key === 'Backspace' && !code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').slice(0, length);

      if (/^\d+$/.test(pastedData)) {
        const newCode = pastedData.split('').concat(Array(length).fill('')).slice(0, length);
        setCode(newCode);
        onChange(newCode.join(''));

        // Focus the last filled input or first empty one
        const lastIndex = Math.min(pastedData.length - 1, length - 1);
        inputRefs.current[lastIndex]?.focus();
      }
    };

    return (
      <div className="w-full">
        {label && <label className="block text-sm font-medium text-gray-700 mb-4">{label}</label>}
        <div className="flex space-x-4 justify-center">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => {
                inputRefs.current[index] = el;
              }}
              type="text"
              maxLength={1}
              value={digit}
              onChange={e => handleInputChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className="w-16 h-16 text-center text-2xl font-semibold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
            />
          ))}
        </div>
        {error && <p className="mt-4 text-sm text-red-600 text-center">{error}</p>}
      </div>
    );
  }
);

VerificationCodeInput.displayName = 'VerificationCodeInput';

export default VerificationCodeInput;
